import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Label } from './label';
import { Input } from './input';
import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import UploadIcon from '../../assets/icons/upload-cloud-02.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
import TrashIcon from '../../assets/icons/trash-02.svg?react';
import clsx from 'clsx';
import { compressImageForUpload } from '@/utils/images/compressImageForUpload';

interface ControllerFileInputProps<FieldsType extends FieldValues> {
  title?: string;
  name?: string;
  field: ControllerRenderProps<FieldsType, Path<FieldsType>>;
  placeholder?: string;
  errorMessage?: string;
  multiple: boolean;
  accept: string;
  disabled?: boolean;
}

const isFileLike = (value: unknown): value is File | Blob =>
  value instanceof Blob;

const ControllerDropZone = <FieldsType extends FieldValues>({
  title,
  field,
  name,
  placeholder,
  errorMessage,
  multiple,
  accept,
  disabled,
}: ControllerFileInputProps<FieldsType>) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragDepthRef = useRef(0);

  // Stable object URLs per Blob — recreating blob: URLs on re-render cancels
  // native drag when the <img> src changes mid-drag.
  const objectUrlsRef = useRef<Map<Blob, string>>(new Map());
  const fileKeysRef = useRef<WeakMap<Blob, string>>(new WeakMap());
  const fileKeyCounterRef = useRef(0);

  const getObjectUrl = useCallback((file: Blob) => {
    const cached = objectUrlsRef.current.get(file);
    if (cached) return cached;
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.set(file, url);
    return url;
  }, []);

  const revokeObjectUrl = useCallback((file: Blob) => {
    const url = objectUrlsRef.current.get(file);
    if (url) {
      URL.revokeObjectURL(url);
      objectUrlsRef.current.delete(file);
    }
    fileKeysRef.current.delete(file);
  }, []);

  const getStableKey = useCallback((file: File | string) => {
    if (file instanceof Blob) {
      let key = fileKeysRef.current.get(file);
      if (!key) {
        key = `blob-${++fileKeyCounterRef.current}`;
        fileKeysRef.current.set(file, key);
      }
      return key;
    }
    return `url-${file}`;
  }, []);

  useEffect(() => {
    const cache = objectUrlsRef.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    e.stopPropagation();
    setDraggedIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    if (draggedIndex === null) {
      setIsDraggingOver(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = draggedIndex !== null ? 'move' : 'copy';
    }
    // Re-rendering while reordering (bg highlight) can cancel native drag.
    if (draggedIndex === null) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0 && draggedIndex === null) {
      setIsDraggingOver(false);
    }
  };

  const commitFiles = useCallback(
    (files: Array<File | string>) => {
      field.onChange({
        target: {
          files: multiple ? files.slice(0, 50) : files.slice(0, 1),
        },
      });
    },
    [field, multiple],
  );

  const processIncomingFiles = useCallback(
    async (incoming: File[]) => {
      if (incoming.length === 0) return;
      const compressed = await Promise.all(
        incoming.map(async (file) => {
          try {
            return await compressImageForUpload(file);
          } catch {
            return file;
          }
        }),
      );
      const current: Array<File | string> = Array.isArray(field.value)
        ? field.value.filter(
            (item: unknown): item is File | string =>
              isFileLike(item) || typeof item === 'string',
          )
        : [];
      commitFiles(multiple ? [...current, ...compressed] : compressed);
    },
    [commitFiles, field.value, multiple],
  );

  const handleOnChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      await processIncomingFiles(Array.from(files));
      e.target.value = '';
    },
    [processIncomingFiles],
  );

  const handleDrop = async (e: React.DragEvent, index?: number) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingOver(false);

    if (draggedIndex !== null && index !== undefined && files.length > 0) {
      const reorderedFiles = [...files];
      const [movedItem] = reorderedFiles.splice(draggedIndex, 1);
      reorderedFiles.splice(index, 0, movedItem);
      commitFiles(reorderedFiles);
      setDraggedIndex(null);
      return;
    }

    setDraggedIndex(null);

    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/'),
    );
    if (droppedFiles.length === 0) return;

    await processIncomingFiles(droppedFiles);
  };

  const files: Array<File | string> = Array.isArray(field.value)
    ? field.value.filter(
        (item: unknown): item is File | string =>
          isFileLike(item) || typeof item === 'string',
      )
    : [];

  return (
    <>
      {title && (
        <Label className="text-gray-700 transition-all duration-200 ease-in-out">
          {title}
        </Label>
      )}
      <div
        className={clsx(
          'flex flex-wrap items-center justify-start w-full gap-4 border px-6 py-4 rounded-xl cursor-pointer',
          {
            'bg-gray-200': isDraggingOver && !disabled,
            'border-error-300': errorMessage,
            'border-[#E2E8F0]': !isDraggingOver && !disabled,
            'bg-white hover:bg-gray-100': !disabled,
            'bg-gray-100 cursor-not-allowed': disabled,
          },
        )}
        onDragEnter={!disabled ? handleDragEnter : undefined}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDragLeave={!disabled ? handleDragLeave : undefined}
        onDrop={!disabled ? (e) => void handleDrop(e) : undefined}
      >
        {multiple && files.length > 0 ? (
          <>
            {files.map((file, index) => (
              <div
                className="relative z-10 shrink-0 cursor-grab active:cursor-grabbing"
                key={getStableKey(file)}
                draggable={!disabled}
                onDragStart={!disabled ? handleDragStart(index) : undefined}
                onDragEnd={!disabled ? handleDragEnd : undefined}
                onDragOver={!disabled ? handleDragOver : undefined}
                onDrop={
                  !disabled ? (e) => void handleDrop(e, index) : undefined
                }
              >
                <img
                  src={isFileLike(file) ? getObjectUrl(file) : file}
                  alt=""
                  draggable={false}
                  className="pointer-events-none h-24 w-auto border border-brand-600 rounded-md select-none"
                />
                {!disabled && (
                  <div
                    className="absolute top-1 right-1 flex items-center justify-center p-1 bg-white rounded-full"
                    onClick={() => {
                      if (isFileLike(file)) {
                        revokeObjectUrl(file);
                      }
                      commitFiles(
                        files.filter(
                          (_file: File | string, i: number) => i !== index,
                        ),
                      );
                    }}
                  >
                    <TrashIcon className="w-3 h-3 stroke-brand-600" />
                  </div>
                )}
              </div>
            ))}
            <label
              htmlFor={`dropzone-file-${name}`}
              className={clsx(
                'relative z-0 flex items-center justify-start grow flex-wrap',
                {
                  'border-error-300': errorMessage,
                },
              )}
            >
              {!disabled && files.length < 50 && (
                <div className="flex items-center justify-center w-10 h-10 bg-white border border-brand-600 rounded-full shadow-xs">
                  <PlusIcon className="w-5 h-5 fill-brand-600" />
                </div>
              )}
              <Input
                id={`dropzone-file-${name}`}
                type="file"
                className="hidden"
                multiple={multiple}
                accept={accept}
                disabled={disabled}
                onChange={handleOnChange}
              />
            </label>
          </>
        ) : !multiple && field.value ? (
          <div className="relative">
            <img
              src={
                isFileLike(field.value)
                  ? getObjectUrl(field.value)
                  : (field.value as string)
              }
              alt=""
              draggable={false}
              className="pointer-events-none h-24 w-auto select-none"
            />
            {!disabled && (
              <div
                className="absolute top-1 right-1 flex items-center justify-center p-1 bg-white rounded-full"
                onClick={() => {
                  if (isFileLike(field.value)) {
                    revokeObjectUrl(field.value);
                  }
                  field.onChange(undefined);
                }}
              >
                <TrashIcon className="w-3 h-3 stroke-brand-600" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center w-full">
            <label
              className={clsx(
                'flex flex-col items-center justify-center w-full px-6 py-4 gap-1 rounded-xl cursor-pointer hover:bg-gray-100',
                {
                  'border-error-300': errorMessage,
                  'bg-gray-200': isDraggingOver,
                },
              )}
            >
              {!disabled ? (
                <>
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-white border rounded-lg border-gray-200 shadow-xs">
                      <UploadIcon className="w-5 h-5" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm leading-6 text-gray-600">
                        <span className="font-semibold text-brand-700">
                          Cliquer pour ajouter
                        </span>{' '}
                        ou drag and drop
                      </p>
                      <p className="text-xs text-gray-600">{placeholder}</p>
                    </div>
                  </div>
                  <Input
                    id={`dropzone-file-${name}`}
                    type="file"
                    className="hidden"
                    multiple={multiple}
                    accept={accept}
                    disabled={disabled}
                    onChange={handleOnChange}
                  />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 cursor-not-allowed">
                  <div className="text-center">
                    <p className="text-sm leading-6 text-gray-600">
                      <span className="font-semibold text-brand-700">
                        Aucune image téléchargée
                      </span>{' '}
                    </p>
                  </div>
                </div>
              )}
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default ControllerDropZone;
